import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { FiSend, FiUsers, FiLoader } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { leadsAPI } from '../services/api';
import theme from '../styles/theme';
import Layout from '../components/Layout';
import { LeadCard, LeadsPrepararMensagensFiltros, LeadsPaginacao } from '../components/leads';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-bottom: ${theme.spacing[4]};
`;

const ContinuarButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${(props) => (props.$disabled ? theme.colors.background.tertiary : theme.colors.primary[500])};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  opacity: ${(props) => (props.$disabled ? 0.6 : 1)};

  &:hover:not(:disabled) {
    background: ${theme.colors.primary[600]};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${theme.colors.primary[500]}40;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const SelecaoBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[4]};
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
`;

const SelecaoLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 500;
  cursor: pointer;

  input {
    width: 18px;
    height: 18px;
    accent-color: ${theme.colors.primary[500]};
    cursor: pointer;
  }
`;

const SelecaoCount = styled.span`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${theme.spacing[6]};
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  color: ${theme.colors.text.muted};
`;

const LoadingSpinner = styled.div`
  font-size: ${theme.fontSizes['2xl']};
  animation: spin 1s linear infinite;
  margin-bottom: ${theme.spacing[4]};

  @keyframes spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.p`
  font-size: ${theme.fontSizes.base};
  margin: 0;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const EmptyIcon = styled.div`
  font-size: ${theme.fontSizes['4xl']};
  color: ${theme.colors.text.muted};
  margin-bottom: ${theme.spacing[4]};
`;

const EmptyTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const EmptyText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${theme.spacing[12]} ${theme.spacing[6]};
  text-align: center;
`;

const ErrorTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.xl};
  font-weight: 600;
  margin: 0 0 ${theme.spacing[2]} 0;
`;

const ErrorText = styled.p`
  color: ${theme.colors.text.muted};
  font-size: ${theme.fontSizes.base};
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ErrorAction = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  background: ${theme.colors.error[500]};
  color: ${theme.colors.text.primary};
  border: none;
  border-radius: ${theme.borderRadius.xl};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: ${theme.colors.error[600]};
  }
`;

const LeadsPrepararMensagens = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [funisList, setFunisList] = useState([]);
  const [etapasList, setEtapasList] = useState([]);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [loadingSelecionarTodos, setLoadingSelecionarTodos] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10,
    hasNextPage: false,
    hasPrevPage: false,
  });

  const [filtros, setFiltros] = useState({
    search: '',
    funil_id: '',
    etapa_atual_id: '',
    empresa: '',
    cidade: '',
    estado: '',
    page: 1,
    limit: 10,
  });

  const ultimaRequisicaoRef = useRef(null);

  const carregarLeads = async (params) => {
    const chaveRequisicao = JSON.stringify(params);
    ultimaRequisicaoRef.current = chaveRequisicao;

    try {
      setLoading(true);
      setError(null);
      const response = await leadsAPI.listar(params);

      if (ultimaRequisicaoRef.current !== chaveRequisicao) {
        return;
      }

      if (response.data.success) {
        setLeads(response.data.data);
        setPagination(response.data.pagination);
      } else {
        setError('Erro ao carregar leads');
      }
    } catch (err) {
      if (ultimaRequisicaoRef.current !== chaveRequisicao) return;
      console.error('Erro ao carregar leads:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      if (ultimaRequisicaoRef.current === chaveRequisicao) {
        setLoading(false);
      }
    }
  };

  const carregarFunis = async () => {
    try {
      const response = await leadsAPI.listarFunis();
      if (response.data.success) {
        setFunisList(response.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar funis:', err);
    }
  };

  const carregarEtapas = async (funilId) => {
    if (!funilId) {
      setEtapasList([]);
      return;
    }
    try {
      const response = await leadsAPI.listarEtapas(funilId);
      if (response.data.success) {
        setEtapasList(response.data.data);
      } else {
        setEtapasList([]);
      }
    } catch (err) {
      console.error('Erro ao carregar etapas:', err);
      setEtapasList([]);
    }
  };

  useEffect(() => {
    carregarLeads(filtros);
    carregarFunis();
  }, []);

  useEffect(() => {
    carregarEtapas(filtros.funil_id);
  }, [filtros.funil_id]);

  const handleFiltrosChange = (novosFiltros) => {
    setFiltros({
      ...novosFiltros,
      page: 1,
      limit: filtros.limit || 10,
    });
  };

  const handleBuscar = (param = null) => {
    if (param && typeof param === 'object' && typeof param.preventDefault === 'function') {
      param.preventDefault();
      const filtrosComPage1 = { ...filtros, page: 1 };
      setFiltros(filtrosComPage1);
      carregarLeads(filtrosComPage1);
      return;
    }
    const filtrosParaBusca = param || filtros;
    carregarLeads(filtrosParaBusca);
  };

  const handlePageChange = (page) => {
    const novosFiltros = { ...filtros, page };
    setFiltros(novosFiltros);
    carregarLeads(novosFiltros);
  };

  const handleItemsPerPageChange = (newLimit) => {
    const novosFiltros = {
      ...filtros,
      limit: newLimit,
      page: 1,
    };
    setFiltros(novosFiltros);
    carregarLeads(novosFiltros);
  };

  const handleToggleLead = (leadId, checked) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(leadId);
      else next.delete(leadId);
      return next;
    });
  };

  const handleSelecionarTodos = async (e) => {
    if (e.target.checked) {
      const total = pagination.totalItems;
      if (total <= 0) return;
      setLoadingSelecionarTodos(true);
      try {
        const params = {
          ...filtros,
          page: 1,
          limit: total,
        };
        const response = await leadsAPI.listar(params);
        if (response.data.success && response.data.data?.length) {
          const todosIds = response.data.data.map((l) => l.id);
          setSelectedIds(new Set(todosIds));
        }
      } catch (err) {
        console.error('Erro ao selecionar todos do filtro:', err);
      } finally {
        setLoadingSelecionarTodos(false);
      }
    } else {
      setSelectedIds(new Set());
    }
  };

  const todosDoFiltroSelecionados =
    pagination.totalItems > 0 && selectedIds.size === pagination.totalItems;
  const algumSelecionado = selectedIds.size > 0;

  const handleContinuar = () => {
    if (!algumSelecionado) return;
    const ids = Array.from(selectedIds);
    navigate('/leads/preparar-mensagens/confirmar-envio', {
      state: {
        leadIdsParaMensagens: ids,
        totalLeadsSelecionados: ids.length
      }
    });
  };

  const renderContent = () => {
    if (loading) {
      return (
        <LoadingContainer>
          <LoadingSpinner>
            <FiLoader />
          </LoadingSpinner>
          <LoadingText>Carregando leads...</LoadingText>
        </LoadingContainer>
      );
    }

    if (error) {
      return (
        <ErrorState>
          <ErrorTitle>Erro ao carregar leads</ErrorTitle>
          <ErrorText>{error}</ErrorText>
          <ErrorAction onClick={() => carregarLeads(filtros)}>Tentar novamente</ErrorAction>
        </ErrorState>
      );
    }

    if (leads.length === 0) {
      return (
        <EmptyState>
          <EmptyIcon>
            <FiUsers />
          </EmptyIcon>
          <EmptyTitle>Nenhum lead encontrado</EmptyTitle>
          <EmptyText>
            Ajuste os filtros por etapa ou busque por outro funil/etapa para listar leads.
          </EmptyText>
        </EmptyState>
      );
    }

    return (
      <>
        <SelecaoBar>
          <SelecaoLabel>
            <input
              type="checkbox"
              checked={todosDoFiltroSelecionados}
              onChange={handleSelecionarTodos}
              disabled={loadingSelecionarTodos || pagination.totalItems <= 0}
            />
            {loadingSelecionarTodos
              ? 'Carregando...'
              : `Selecionar todos do filtro (${pagination.totalItems} registro(s))`}
          </SelecaoLabel>
          <SelecaoCount>{selectedIds.size} lead(s) selecionado(s)</SelecaoCount>
        </SelecaoBar>

        <CardsGrid>
          {leads.map((lead) => (
            <LeadCard
              key={lead.id}
              lead={lead}
              selectable
              selected={selectedIds.has(lead.id)}
              onToggle={handleToggleLead}
            />
          ))}
        </CardsGrid>

        {pagination.totalPages > 1 && (
          <LeadsPaginacao
            pagination={pagination}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            loading={loading}
          />
        )}
      </>
    );
  };

  return (
    <Layout title="Preparar Leads para Mensagens">
      <Container>
        <Header>
          <ContinuarButton
            type="button"
            $disabled={!algumSelecionado}
            disabled={!algumSelecionado}
            onClick={handleContinuar}
          >
            <FiSend />
            Continuar com {selectedIds.size} selecionado(s)
          </ContinuarButton>
        </Header>

        <Content>
          <LeadsPrepararMensagensFiltros
            filtros={filtros}
            funisList={funisList}
            etapasList={etapasList}
            onFiltrosChange={handleFiltrosChange}
            onBuscar={handleBuscar}
            loading={loading}
          />

          {renderContent()}
        </Content>
      </Container>
    </Layout>
  );
};

export default LeadsPrepararMensagens;
